import { useState } from 'react'
import { LensPicker } from '@/components/LensPicker'
import { Recommender } from '@/components/Recommender'
import { Simulator } from '@/components/Simulator'
import { DEFAULT_CAMERA_STATE } from '@/domain/state'
import { clampFocal, type Lens } from '@/domain/lens'
import { loadLens, saveLens } from '@/services/lensPreference'
import type { CameraState } from '@/domain/types'

type View = 'split' | 'dials'

export default function App() {
  const [view, setView] = useState<View>('split')
  const [lens, setLens] = useState<Lens>(loadLens)
  const [dialState, setDialState] = useState<CameraState>(DEFAULT_CAMERA_STATE)
  // Bumping this remounts the simulator so a loaded recipe takes effect.
  const [loadCount, setLoadCount] = useState(0)

  const changeLens = (l: Lens) => {
    setLens(l)
    saveLens(l)
  }

  const openInDials = (state: CameraState) => {
    setDialState(state)
    setLoadCount((n) => n + 1)
    setView('dials')
  }

  if (view === 'dials') {
    return (
      <div className="min-h-dvh bg-ink">
        <div className="mx-auto w-full max-w-2xl px-4 safe-top safe-bottom">
          <header className="flex items-center justify-between py-3">
            <button
              onClick={() => setView('split')}
              className="text-[13px] font-medium text-muted transition-colors hover:text-chrome"
            >
              ← Back to settings
            </button>
            <span className="text-[10px] font-bold uppercase tracking-[.2em] text-muted">
              Learn the dials
            </span>
          </header>
          <div className="mb-3">
            <LensPicker lens={lens} onChange={changeLens} />
          </div>
          <Simulator
            key={`${loadCount}-${lens.id}`}
            lens={lens}
            initialState={{ ...dialState, focalLength: clampFocal(lens, dialState.focalLength) }}
          />
          <footer className="mt-8 border-t border-edge pt-4 pb-2 text-[11px] leading-relaxed text-muted">
            Behaviour follows the Nikon Z fc Reference Guide. Lens modelled: {lens.name}.
          </footer>
        </div>
      </div>
    )
  }

  return (
    <div className="h-dvh overflow-hidden bg-ink">
      <div className="mx-auto h-full w-full max-w-2xl safe-top">
        <Recommender lens={lens} onLensChange={changeLens} onOpenInDials={openInDials} />
      </div>
    </div>
  )
}
